// src/lib/webVitals.ts
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

import { captureMessage } from '@/lib/sentry';

type WebVitalsMetric = {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
};

/**
 * Report Web Vitals to analytics
 * Sends metrics to Google Analytics and Sentry
 */
function reportWebVitals(metric: Metric): void {
  const vitalsData: WebVitalsMetric = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
  };

  // Log in development
  if (import.meta.env.DEV) {
    const colorStyle =
      metric.rating === 'good' ? 'color: green' :
      metric.rating === 'needs-improvement' ? 'color: orange' :
      'color: red';

    // eslint-disable-next-line no-console -- dev-only diagnostics
    console.log(
      `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
      colorStyle
    );
  }

  // Send to Google Analytics if available
  if (typeof window !== 'undefined' && 'gtag' in window) {
    const gtag = (window as unknown as { gtag: (...args: unknown[]) => void }).gtag;
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }

  // Report poor metrics to Sentry for alerting
  if (metric.rating === 'poor' && !import.meta.env.DEV) {
    captureMessage(
      `Poor Web Vital: ${metric.name}`,
      'warning',
      vitalsData
    );
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when the app starts
 */
export function initWebVitals(): void {
  // Core Web Vitals
  onCLS(reportWebVitals);  // Cumulative Layout Shift
  onLCP(reportWebVitals);  // Largest Contentful Paint
  onINP(reportWebVitals);  // Interaction to Next Paint (replaces FID)

  // Additional metrics
  onFCP(reportWebVitals);  // First Contentful Paint
  onTTFB(reportWebVitals); // Time to First Byte
}

/**
 * Web Vitals thresholds for reference
 *
 * LCP (Largest Contentful Paint):
 *   - Good: ≤ 2.5s
 *   - Needs Improvement: 2.5s - 4s
 *   - Poor: > 4s
 *
 * INP (Interaction to Next Paint):
 *   - Good: ≤ 200ms
 *   - Needs Improvement: 200ms - 500ms
 *   - Poor: > 500ms
 *
 * CLS (Cumulative Layout Shift):
 *   - Good: ≤ 0.1
 *   - Needs Improvement: 0.1 - 0.25
 *   - Poor: > 0.25
 *
 * FCP (First Contentful Paint):
 *   - Good: ≤ 1.8s
 *   - Needs Improvement: 1.8s - 3s
 *   - Poor: > 3s
 *
 * TTFB (Time to First Byte):
 *   - Good: ≤ 800ms
 *   - Needs Improvement: 800ms - 1800ms
 *   - Poor: > 1800ms
 */
